import React, { useState } from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Search, Filter, X, ChevronDown, ChevronUp, Check } from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export default function RitualSearch({ rituals, officers, onFilteredResults }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedCadence, setSelectedCadence] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedOwner, setSelectedOwner] = useState('all');
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Get unique values for filters
  const allOwners = [...new Set(rituals.map((r) => r.owner_email))];

  React.useEffect(() => {
    const filtered = rituals.filter((ritual) => {
      // Text search (name, description)
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesName = ritual.name.toLowerCase().includes(query);
        const matchesDescription = ritual.description?.toLowerCase().includes(query);
        const matchesType = ritual.type?.toLowerCase().includes(query);

        if (!matchesName && !matchesDescription && !matchesType) {
          return false;
        }
      }

      // Type filter
      if (selectedType !== 'all' && ritual.type !== selectedType) {
        return false;
      }

      // Cadence filter
      if (selectedCadence !== 'all' && ritual.cadence !== selectedCadence) {
        return false;
      }

      // Status filter
      if (selectedStatus !== 'all' && ritual.status !== selectedStatus) {
        return false;
      }

      // Owner filter
      if (selectedOwner !== 'all' && ritual.owner_email !== selectedOwner) {
        return false;
      }

      // Participants filter
      if (selectedParticipants.length > 0) {
        const hasParticipant = selectedParticipants.some((p) => ritual.participants?.includes(p));
        if (!hasParticipant) return false;
      }

      return true;
    });

    onFilteredResults(filtered);
  }, [searchQuery, selectedType, selectedCadence, selectedStatus, selectedOwner, selectedParticipants, rituals]);

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedType('all');
    setSelectedCadence('all');
    setSelectedStatus('all');
    setSelectedOwner('all');
    setSelectedParticipants([]);
  };

  const hasActiveFilters = searchQuery || selectedType !== 'all' || selectedCadence !== 'all' ||
    selectedStatus !== 'all' || selectedOwner !== 'all' || selectedParticipants.length > 0;

  return (
    <Card className="glass-panel border-slate-800 mb-6">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input data-testid="art-ritualsearch-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search rituals by name, description, or type..."
              className="bg-slate-50 pl-10 border-slate-800 text-slate-900 placeholder:text-slate-500"
            />
          </div>
          <Button
            variant="outline"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="border-slate-800"
          >
            <Filter className="w-4 h-4 mr-2" />
            Advanced
            {showAdvanced ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
          </Button>
          {hasActiveFilters && (
            <Button data-testid="art-clear-btn" variant="ghost" onClick={clearFilters} className="text-slate-400">
              <X className="w-4 h-4 mr-2" />
              Clear
            </Button>
          )}
        </div>

        {showAdvanced && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t border-slate-800">
            <div className="space-y-2">
              <Label className="text-slate-600 text-xs font-medium">Type</Label>
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger className="bg-slate-50 border-slate-800">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="meeting">Meeting</SelectItem>
                  <SelectItem value="standup">Stand-up</SelectItem>
                  <SelectItem value="review">Review</SelectItem>
                  <SelectItem value="all_hands">All Hands</SelectItem>
                  <SelectItem value="workshop">Workshop</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-600 text-xs font-medium">Cadence</Label>
              <Select value={selectedCadence} onValueChange={setSelectedCadence}>
                <SelectTrigger className="bg-slate-50 border-slate-800">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cadences</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Bi-weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="annually">Annually</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-600 text-xs font-medium">Status</Label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger className="bg-slate-50 border-slate-800">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="pending_approval">Pending Approval</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="institutionalized">Institutionalized</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-600 text-xs font-medium">Owner</Label>
              <Select value={selectedOwner} onValueChange={setSelectedOwner}>
                <SelectTrigger className="bg-slate-50 border-slate-800">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Owners</SelectItem>
                  {allOwners.map((email) => {
                    const officer = officers.find((o) => o.user_email === email);
                    return (
                      <SelectItem key={email} value={email}>
                        {officer?.callsign || email}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-600 text-xs font-medium">Has Participant</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start bg-slate-50 border-slate-800">
                    {selectedParticipants.length === 0 ? 'Select participants...' : `${selectedParticipants.length} selected`}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0 bg-slate-900 border-slate-800">
                  <Command>
                    <CommandInput placeholder="Search officers..." />
                    <CommandList>
                      <CommandEmpty>No officers found.</CommandEmpty>
                      <CommandGroup>
                        {officers.map((officer) => (
                          <CommandItem
                            key={officer.id}
                            onSelect={() => {
                              setSelectedParticipants((prev) =>
                                prev.includes(officer.user_email)
                                  ? prev.filter((e) => e !== officer.user_email)
                                  : [...prev, officer.user_email]
                              );
                            }}
                          >
                            <div className="flex items-center gap-2 w-full">
                              <div
                                className={`w-4 h-4 border rounded flex items-center justify-center ${
                                  selectedParticipants.includes(officer.user_email)
                                    ? 'bg-purple-600 border-purple-600'
                                    : 'border-slate-600'
                                }`}
                              >
                                {selectedParticipants.includes(officer.user_email) && <Check className="w-3 h-3 text-white" />}
                              </div>
                              <span>{officer.callsign}</span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {selectedParticipants.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {selectedParticipants.map((email) => {
                    const officer = officers.find((o) => o.user_email === email);
                    return (
                      <Badge key={email} variant="secondary" className="bg-purple-500/10 text-purple-400">
                        {officer?.callsign || email}
                        <button
                          onClick={() => setSelectedParticipants(selectedParticipants.filter((e) => e !== email))}
                          className="ml-1"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
